import { useMemo, useState } from 'react';

import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';

/**
 * 계정 관리 > Permission — 레거시 WebService/auth/permission/list 대응.
 *
 * 레거시 권한은 http_path 자유 문자열을 등록/삭제하는 화면이었지만, 신규는 권한이
 * 서버 코드(`order.read` …)로 고정된 **읽기 전용 목록**이다 — 어떤 코드가 어떤
 * 요청을 막는지는 서버 ROUTE_PERMISSIONS 가 정하므로 화면에서 만들 것이 없다.
 * 역할에 권한을 붙이는 작업은 Role 화면에서 한다.
 *
 * '보유 역할' 열은 열람자에게 보이는 역할(depth 규칙)만 나온다.
 */
export function PermissionManage() {
  const list = useAsync(() => api.listPermissions(), []);
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');

  // 그룹/권한 이름/코드 어디에든 걸리면 보여준다 — 목록이 60줄이라 서버 검색은 두지 않았다.
  const rows = useMemo(() => {
    const items = list.data ?? [];
    const q = applied.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.group_label.toLowerCase().includes(q) ||
        p.label.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        p.roles.some((r) => r.name.toLowerCase().includes(q)),
    );
  }, [list.data, applied]);

  return (
    <div className="admin__body admin__body--single">
      <section className="admin__list">
        <div className="toolbar">
          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="그룹 · 권한 · 코드 · 역할"
            />
            <button type="submit">검색</button>
          </form>
          <span className="muted">
            권한은 서버 코드로 고정되어 있어 등록/삭제할 수 없습니다. 역할별 부여는 Role
            메뉴에서 합니다.
          </span>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>그룹</th>
                <th>권한</th>
                <th>코드</th>
                <th>보유 역할</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    조건에 맞는 권한이 없습니다.
                  </td>
                </tr>
              )}
              {rows.map((p, i) => {
                // 같은 그룹이 이어지면 그룹명은 첫 줄에만 — 레거시 격자와 같은 읽기 흐름.
                const firstOfGroup = i === 0 || rows[i - 1]?.group_label !== p.group_label;
                return (
                  <tr key={p.code}>
                    <td>{firstOfGroup ? <strong>{p.group_label}</strong> : ''}</td>
                    <td>{p.label}</td>
                    <td>
                      <code>{p.code}</code>
                    </td>
                    <td className="addr">
                      {p.roles.length === 0 ? (
                        <span className="muted">-</span>
                      ) : (
                        p.roles.map((r) => (
                          <span key={r.id} className="badge">
                            {r.name}
                          </span>
                        ))
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
